import jsPDF from 'jspdf';
import { BRAND } from '@/config/brand';
import { guideSections, faqItems, USER_GUIDE_VERSION, USER_GUIDE_DATE, GuideSection } from '@/data/userGuideContent';

// Import fonts
import NotoSansRegular from '@/assets/fonts/NotoSans-Regular.ttf';
import NotoSansBold from '@/assets/fonts/NotoSans-Bold.ttf';

const COLORS = {
  primary: BRAND.colors.forestGreen,
  secondary: BRAND.colors.sageGreen,
  gold: BRAND.colors.gold,
  text: '#1a1a1a',
  lightBg: BRAND.colors.lightBackground,
  muted: '#6b7280',
};

export async function generateUserGuidePDF(): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Load fonts
  const loadFont = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const buffer = await response.arrayBuffer();
    const binary = new Uint8Array(buffer);
    let result = '';
    for (let i = 0; i < binary.length; i++) {
      result += String.fromCharCode(binary[i]);
    }
    return btoa(result);
  };

  try {
    const regularFont = await loadFont(NotoSansRegular);
    const boldFont = await loadFont(NotoSansBold);
    
    doc.addFileToVFS('NotoSans-Regular.ttf', regularFont);
    doc.addFileToVFS('NotoSans-Bold.ttf', boldFont);
    doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal');
    doc.addFont('NotoSans-Bold.ttf', 'NotoSans', 'bold');
  } catch (e) {
    console.warn('Could not load custom fonts, using defaults');
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let currentY = margin;
  let currentPage = 1;

  // Track sections for TOC
  const tocEntries: { title: string; page: number }[] = [];

  const addNewPage = () => {
    doc.addPage();
    currentPage++;
    currentY = margin;
    addFooter();
  };

  const checkPageBreak = (neededSpace: number) => {
    if (currentY + neededSpace > pageHeight - 25) {
      addNewPage();
      return true;
    }
    return false;
  };

  const addFooter = () => {
    const footerY = pageHeight - 10;
    doc.setFont('NotoSans', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(COLORS.muted);
    doc.text(`${BRAND.name.full} - Guide d'utilisation v${USER_GUIDE_VERSION}`, margin, footerY);
    doc.text(`Page ${currentPage}`, pageWidth - margin, footerY, { align: 'right' });
  };

  // === COVER PAGE ===
  const addCoverPage = () => {
    // Background
    doc.setFillColor(COLORS.lightBg);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    
    // Header band
    doc.setFillColor(COLORS.primary);
    doc.rect(0, 0, pageWidth, 60, 'F');
    
    // Title on header
    doc.setFont('NotoSans', 'bold');
    doc.setFontSize(28);
    doc.setTextColor('#ffffff');
    doc.text(BRAND.name.full, pageWidth / 2, 35, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text(BRAND.baseline.full, pageWidth / 2, 48, { align: 'center' });
    
    // Main title
    doc.setTextColor(COLORS.primary);
    doc.setFontSize(32);
    doc.text("Guide d'Utilisation", pageWidth / 2, 100, { align: 'center' });
    
    doc.setFontSize(18);
    doc.setTextColor(COLORS.secondary);
    doc.text("Espace Professionnel", pageWidth / 2, 115, { align: 'center' });
    
    // Version box
    doc.setFillColor(COLORS.gold);
    doc.roundedRect(pageWidth / 2 - 30, 135, 60, 20, 3, 3, 'F');
    doc.setFontSize(12);
    doc.setTextColor('#ffffff');
    doc.text(`Version ${USER_GUIDE_VERSION}`, pageWidth / 2, 147, { align: 'center' });
    
    // Date
    doc.setFontSize(11);
    doc.setTextColor(COLORS.muted);
    doc.text(USER_GUIDE_DATE, pageWidth / 2, 170, { align: 'center' });
    
    // Description box
    doc.setFillColor('#ffffff');
    doc.roundedRect(margin, 190, contentWidth, 50, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setTextColor(COLORS.text);
    doc.setFont('NotoSans', 'normal');
    const introText = "Ce guide vous accompagne dans l'utilisation de votre espace professionnel. Vous y trouverez les instructions pour chaque fonctionnalité, les bonnes pratiques à adopter et les erreurs courantes à éviter.";
    const introLines = doc.splitTextToSize(introText, contentWidth - 10);
    doc.text(introLines, margin + 5, 205);
    
    // Footer
    doc.setFontSize(9);
    doc.setTextColor(COLORS.muted);
    doc.text("Document confidentiel - Usage professionnel uniquement", pageWidth / 2, pageHeight - 20, { align: 'center' });
    
    addFooter();
  };

  // === TABLE OF CONTENTS ===
  const addTableOfContents = () => {
    addNewPage();
    const tocStartPage = currentPage;
    
    doc.setFont('NotoSans', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(COLORS.primary);
    doc.text("Sommaire", pageWidth / 2, currentY + 10, { align: 'center' });
    currentY += 25;
    
    doc.setFont('NotoSans', 'normal');
    doc.setFontSize(11);
    
    guideSections.forEach((section, index) => {
      checkPageBreak(8);
      
      const num = String(index + 1).padStart(2, '0');
      doc.setTextColor(COLORS.gold);
      doc.text(num, margin, currentY);
      
      doc.setTextColor(COLORS.text);
      doc.text(`${section.icon} ${section.title}`, margin + 12, currentY);
      
      // Dotted line
      const textWidth = doc.getTextWidth(`${section.icon} ${section.title}`);
      const dotsStart = margin + 12 + textWidth + 2;
      const dotsEnd = pageWidth - margin - 15;
      doc.setDrawColor(COLORS.muted);
      doc.setLineDashPattern([1, 1], 0);
      doc.line(dotsStart, currentY, dotsEnd, currentY);
      doc.setLineDashPattern([], 0);
      
      currentY += 8;
    });
    
    // FAQ entry
    checkPageBreak(8);
    doc.setTextColor(COLORS.gold);
    doc.text(String(guideSections.length + 1).padStart(2, '0'), margin, currentY);
    doc.setTextColor(COLORS.text);
    doc.text("❓ FAQ - Questions Fréquentes", margin + 12, currentY);
    currentY += 15;
  };

  // === SECTION CONTENT ===
  const addSectionContent = (section: GuideSection, index: number) => {
    checkPageBreak(50);
    
    // Section header
    doc.setFillColor(COLORS.primary);
    doc.rect(0, currentY - 5, pageWidth, 18, 'F');
    
    doc.setFont('NotoSans', 'bold');
    doc.setFontSize(14);
    doc.setTextColor('#ffffff');
    doc.text(`${String(index + 1).padStart(2, '0')}. ${section.icon} ${section.title}`, margin, currentY + 5);
    currentY += 20;
    
    // Description
    doc.setFont('NotoSans', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(COLORS.text);
    const descLines = doc.splitTextToSize(section.description, contentWidth);
    descLines.forEach((line: string) => {
      checkPageBreak(6);
      doc.text(line, margin, currentY);
      currentY += 5;
    });
    currentY += 5;
    
    // How to use
    checkPageBreak(20);
    doc.setFillColor(COLORS.lightBg);
    doc.roundedRect(margin, currentY, contentWidth, 8, 2, 2, 'F');
    doc.setFont('NotoSans', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(COLORS.primary);
    doc.text("📋 Comment utiliser", margin + 3, currentY + 5.5);
    currentY += 12;
    
    doc.setFont('NotoSans', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(COLORS.text);
    section.howToUse.forEach((item, i) => {
      checkPageBreak(7);
      doc.setTextColor(COLORS.gold);
      doc.text(`${i + 1}.`, margin + 3, currentY);
      doc.setTextColor(COLORS.text);
      const itemLines = doc.splitTextToSize(item, contentWidth - 15);
      doc.text(itemLines[0], margin + 10, currentY);
      if (itemLines.length > 1) {
        for (let j = 1; j < itemLines.length; j++) {
          currentY += 4;
          checkPageBreak(5);
          doc.text(itemLines[j], margin + 10, currentY);
        }
      }
      currentY += 5;
    });
    currentY += 3;
    
    // Best practices
    checkPageBreak(20);
    doc.setFillColor('#e8f5e9');
    doc.roundedRect(margin, currentY, contentWidth, 8, 2, 2, 'F');
    doc.setFont('NotoSans', 'bold');
    doc.setFontSize(11);
    doc.setTextColor('#2e7d32');
    doc.text("✅ Bonnes pratiques", margin + 3, currentY + 5.5);
    currentY += 12;
    
    doc.setFont('NotoSans', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(COLORS.text);
    section.bestPractices.forEach((item) => {
      checkPageBreak(7);
      doc.setTextColor('#2e7d32');
      doc.text("•", margin + 3, currentY);
      doc.setTextColor(COLORS.text);
      const itemLines = doc.splitTextToSize(item, contentWidth - 12);
      doc.text(itemLines[0], margin + 8, currentY);
      if (itemLines.length > 1) {
        for (let j = 1; j < itemLines.length; j++) {
          currentY += 4;
          checkPageBreak(5);
          doc.text(itemLines[j], margin + 8, currentY);
        }
      }
      currentY += 5;
    });
    currentY += 3;
    
    // Common mistakes
    checkPageBreak(20);
    doc.setFillColor('#ffebee');
    doc.roundedRect(margin, currentY, contentWidth, 8, 2, 2, 'F');
    doc.setFont('NotoSans', 'bold');
    doc.setFontSize(11);
    doc.setTextColor('#c62828');
    doc.text("⚠️ Erreurs à éviter", margin + 3, currentY + 5.5);
    currentY += 12;
    
    doc.setFont('NotoSans', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(COLORS.text);
    section.commonMistakes.forEach((item) => {
      checkPageBreak(7);
      doc.setTextColor('#c62828');
      doc.text("✗", margin + 3, currentY);
      doc.setTextColor(COLORS.text);
      const itemLines = doc.splitTextToSize(item, contentWidth - 12);
      doc.text(itemLines[0], margin + 8, currentY);
      if (itemLines.length > 1) {
        for (let j = 1; j < itemLines.length; j++) {
          currentY += 4;
          checkPageBreak(5);
          doc.text(itemLines[j], margin + 8, currentY);
        }
      }
      currentY += 5;
    });
    
    currentY += 10;
  };

  // === FAQ SECTION ===
  const addFAQSection = () => {
    checkPageBreak(50);
    
    // Section header
    doc.setFillColor(COLORS.primary);
    doc.rect(0, currentY - 5, pageWidth, 18, 'F');
    
    doc.setFont('NotoSans', 'bold');
    doc.setFontSize(14);
    doc.setTextColor('#ffffff');
    doc.text(`${String(guideSections.length + 1).padStart(2, '0')}. ❓ FAQ - Questions Fréquentes`, margin, currentY + 5);
    currentY += 25;
    
    faqItems.forEach((item, index) => {
      checkPageBreak(25);
      
      // Question
      doc.setFillColor(COLORS.lightBg);
      doc.roundedRect(margin, currentY - 3, contentWidth, 10, 2, 2, 'F');
      doc.setFont('NotoSans', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(COLORS.primary);
      doc.text(`Q${index + 1}: ${item.question}`, margin + 3, currentY + 3);
      currentY += 12;
      
      // Answer
      doc.setFont('NotoSans', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(COLORS.text);
      const answerLines = doc.splitTextToSize(item.answer, contentWidth - 10);
      answerLines.forEach((line: string) => {
        checkPageBreak(5);
        doc.text(line, margin + 5, currentY);
        currentY += 4.5;
      });
      currentY += 8;
    });
  };

  // === FINAL PAGE ===
  const addFinalPage = () => {
    addNewPage();
    
    // Thank you message
    doc.setFont('NotoSans', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(COLORS.primary);
    doc.text("Merci d'utiliser", pageWidth / 2, pageHeight / 2 - 20, { align: 'center' });
    doc.text(BRAND.name.full, pageWidth / 2, pageHeight / 2, { align: 'center' });
    
    doc.setFont('NotoSans', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(COLORS.secondary);
    doc.text(BRAND.baseline.full, pageWidth / 2, pageHeight / 2 + 15, { align: 'center' });
    
    // Contact info box
    doc.setFillColor(COLORS.lightBg);
    doc.roundedRect(margin + 20, pageHeight / 2 + 35, contentWidth - 40, 40, 3, 3, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(COLORS.text);
    doc.text("Pour toute question ou assistance :", pageWidth / 2, pageHeight / 2 + 50, { align: 'center' });
    doc.setTextColor(COLORS.gold);
    doc.text("Consultez les paramètres de votre compte", pageWidth / 2, pageHeight / 2 + 60, { align: 'center' });
    
    // Version info
    doc.setFontSize(8);
    doc.setTextColor(COLORS.muted);
    doc.text(`Guide d'utilisation v${USER_GUIDE_VERSION} - ${USER_GUIDE_DATE}`, pageWidth / 2, pageHeight - 30, { align: 'center' });
    doc.text("Document généré automatiquement", pageWidth / 2, pageHeight - 25, { align: 'center' });
  };

  // === GENERATE PDF ===
  addCoverPage();
  addTableOfContents();
  
  guideSections.forEach((section, index) => {
    addSectionContent(section, index);
  });
  
  addFAQSection();
  addFinalPage();
  
  // Save
  doc.save(`Guide-Utilisation-${BRAND.name.full.replace(/\s+/g, '-')}-v${USER_GUIDE_VERSION}.pdf`);
}
